/**
 * Карта физической близости клавиш для классификации ошибок.
 *
 * Строится один раз на раскладку и переиспользуется: обход всех пар клавиш на
 * каждом нажатии обошёлся бы слишком дорого.
 *
 * Соседство считается и для символов, набираемых с Shift (ТЗ FR-EN-44):
 * символ с Shift — та же клавиша, и промах по соседней с ней остаётся
 * промахом по соседней. Без этого ошибки в пунктуации и пакетах
 * классифицировались бы как подстановка вместо соседней клавиши.
 */

import { areAdjacent, distanceBetween } from './geometry.js';
import { keyForCharacter } from './registry.js';
import type { KeyDefinition, KeyboardLayout } from './types.js';

/**
 * Карта строится на объект раскладки, а не на её идентификатор: у вариантов
 * одной раскладки для разных систем идентификатор общий, а соседи разные.
 */
const NEIGHBOUR_CACHE = new Map<KeyboardLayout, Map<string, ReadonlySet<string>>>();

/**
 * Символы клавиши в нижнем регистре: и без Shift, и с ним.
 *
 * Символ с Shift — это та же клавиша, и промах по соседней с ней остаётся
 * промахом по соседней. На Windows знаки препинания стоят без Shift, и разница
 * не была заметна; на macOS точка и запятая набираются с Shift, и без этого
 * половина промахов по ним выглядела бы случайной заменой (ТЗ FR-EN-42).
 */
function charactersOf(key: KeyDefinition): string[] {
  return [key.base, key.shifted]
    .filter((character) => character !== '')
    .map((character) => character.toLowerCase());
}

function buildNeighbours(layout: KeyboardLayout): Map<string, ReadonlySet<string>> {
  const map = new Map<string, Set<string>>();

  for (const key of layout.keys) {
    const characters = charactersOf(key);
    if (characters.length === 0) continue;

    const neighbours = new Set<string>();
    for (const other of layout.keys) {
      if (areAdjacent(key, other)) {
        for (const character of charactersOf(other)) neighbours.add(character);
      }
    }

    // Символы одной клавиши соседями друг другу не считаются: это одно место.
    for (const character of characters) {
      const known = map.get(character);
      if (known === undefined) map.set(character, neighbours);
      else for (const neighbour of neighbours) known.add(neighbour);
    }
  }

  return map;
}

function neighboursFor(layout: KeyboardLayout): Map<string, ReadonlySet<string>> {
  const cached = NEIGHBOUR_CACHE.get(layout);
  if (cached !== undefined) return cached;
  const built = buildNeighbours(layout);
  NEIGHBOUR_CACHE.set(layout, built);
  return built;
}

/** Соседние клавиши для символа (в нижнем регистре раскладки). */
export function neighboursOf(layout: KeyboardLayout, character: string): ReadonlySet<string> {
  return neighboursFor(layout).get(character.toLowerCase()) ?? new Set<string>();
}

/**
 * Являются ли два символа соседними клавишами.
 *
 * Регистр не учитывается: физически это одна и та же клавиша.
 */
export function areNeighbourCharacters(
  layout: KeyboardLayout,
  first: string,
  second: string,
): boolean {
  return neighboursOf(layout, first).has(second.toLowerCase());
}

/**
 * Расстояние между клавишами двух символов в условных единицах.
 * Возвращает null, если хотя бы один символ отсутствует в раскладке.
 */
export function characterDistance(
  layout: KeyboardLayout,
  first: string,
  second: string,
): number | null {
  const a = keyForCharacter(layout, first);
  const b = keyForCharacter(layout, second);
  if (a === null || b === null) return null;
  return distanceBetween(a, b);
}
